import type { ProjectHandle, ProjectSource, ProjectSourceCapabilities } from "@loumy/contracts";
import { normalizeProjectPath } from "./paths";

// D69 — `CliSource`: Client-Adapter, der über localhost-HTTP/WS mit dem `loumy dev`-Server (D73,
// Phase 5) spricht. Cross-Browser + voller Komfort (Watch/Generate, später run). Der SERVER hält die
// verbindlichen Sicherheits-Invarianten (bind 127.0.0.1, Origin-Pinning/CORS, Token, Root-Sandbox);
// dieser CLIENT erzwingt sie defensiv mit (Defense-in-Depth):
//   (1) Base-URL HART auf http://127.0.0.1|localhost[:port] eingegrenzt — die Editor-Seite kann so
//       NICHT auf einen Fremd-Host gerichtet werden.
//   (2) ephemerer Token NUR im Speicher (kein localStorage), als `Authorization: Bearer` — NIE in der
//       URL/Query (Token-in-Query-Leak vermeiden, vgl. Login-Auth-P1); fürs WS First-Message-Auth.
//   (3) credentials:"omit" + mode:"cors" — Token im Header, keine Cookie-/CSRF-Fläche.
//   (4) jeder Pfad client-seitig durch normalizeProjectPath (kein `..`/absoluter Pfad), bevor er rausgeht.
//
// Das CLI↔Editor-Protokoll (Endpunkt-Formen) ist in Editor_PRD §16 als „beim Bau schärfen" markiert;
// dies ist die client-seitige Referenz-Implementierung, die der CLI-Server (Phase 5) bedient.

const CLI_CAPABILITIES: ProjectSourceCapabilities = { write: true, watch: true, generate: true, run: false };

/** Nur localhost-Loopback erlauben (HTTP; localhost braucht kein TLS). Wirft bei allem anderen. */
const LOOPBACK = /^http:\/\/(127\.0\.0\.1|localhost)(:\d{1,5})?$/;

export interface CliConnection {
  /** z.B. "http://127.0.0.1:4317" (ohne Pfad/Trailing-Slash). */
  baseUrl: string;
  /** Ephemerer Session-Token aus der `loumy dev`-Konsole. Nur im Speicher gehalten. */
  token: string;
}

export class CliSource implements ProjectSource {
  readonly id = "cli" as const;
  readonly capabilities = CLI_CAPABILITIES;
  private readonly base: string;
  private readonly token: string;

  constructor(conn: CliConnection) {
    const base = conn.baseUrl.replace(/\/+$/, "");
    if (!LOOPBACK.test(base)) {
      // Die abgelehnte URL NICHT echoen (könnte Credentials/Query enthalten).
      throw new Error("CLI-Quelle erlaubt nur localhost-Loopback (http://127.0.0.1[:port] oder http://localhost[:port]).");
    }
    if (!conn.token || conn.token.length < 8) throw new Error("CLI-Session-Token fehlt oder ist zu kurz.");
    this.base = base;
    this.token = conn.token;
  }

  private authHeaders(extra: Record<string, string> = {}): Record<string, string> {
    return { Authorization: `Bearer ${this.token}`, ...extra };
  }

  private async req(path: string, init: RequestInit): Promise<Response> {
    const res = await fetch(`${this.base}${path}`, { mode: "cors", credentials: "omit", ...init });
    if (!res.ok) throw new Error(`CLI-Quelle: ${init.method ?? "GET"} ${path} → HTTP ${res.status}`);
    return res;
  }

  async open(): Promise<ProjectHandle> {
    const res = await this.req("/project", { method: "GET", headers: this.authHeaders() });
    const info = (await res.json()) as { id?: unknown; name?: unknown };
    if (typeof info?.id !== "string" || typeof info?.name !== "string") {
      throw new Error("CLI-Quelle: unerwartete /project-Antwort (id/name fehlen).");
    }
    return { id: info.id, name: info.name };
  }

  async readFile(path: string): Promise<Uint8Array> {
    const safe = normalizeProjectPath(path);
    const res = await this.req(`/file?path=${encodeURIComponent(safe)}`, { method: "GET", headers: this.authHeaders() });
    return new Uint8Array(await res.arrayBuffer());
  }

  async writeFile(path: string, data: Uint8Array): Promise<void> {
    const safe = normalizeProjectPath(path);
    await this.req(`/file?path=${encodeURIComponent(safe)}`, {
      method: "PUT",
      headers: this.authHeaders({ "Content-Type": "application/octet-stream" }),
      body: data.slice(),
    });
  }

  async removeFile(path: string): Promise<void> {
    const safe = normalizeProjectPath(path);
    await this.req(`/file?path=${encodeURIComponent(safe)}`, { method: "DELETE", headers: this.authHeaders() });
  }

  async list(glob: string): Promise<string[]> {
    const res = await this.req(`/list?glob=${encodeURIComponent(glob)}`, { method: "GET", headers: this.authHeaders() });
    const body = (await res.json()) as { paths?: unknown };
    if (!Array.isArray(body?.paths)) throw new Error("CLI-Quelle: unerwartete /list-Antwort (paths fehlt).");
    return body.paths.filter((p): p is string => typeof p === "string");
  }

  async generate(): Promise<void> {
    await this.req("/generate", { method: "POST", headers: this.authHeaders() });
  }

  /**
   * Watch über WebSocket. Token NICHT in der URL — Auth über die erste Nachricht ({type:"auth"}).
   * Das Abo ({type:"watch", glob}) wird ERST nach der Server-Bestätigung ({type:"auth-ok"}) gesendet
   * (kein Daten-/Abo-Verkehr vor abgeschlossenem Handshake, D69-Invariante #3). Der Server sendet danach
   * {type:"changed", paths:[…]}. Rückgabe: Unsubscribe (schließt den Socket). Kein WebSocket → no-op.
   */
  watch(glob: string, cb: (paths: string[]) => void): () => void {
    if (typeof WebSocket === "undefined") return () => {};
    const wsBase = this.base.replace(/^http/, "ws");
    const socket = new WebSocket(`${wsBase}/watch`);
    let authed = false;
    socket.addEventListener("open", () => {
      socket.send(JSON.stringify({ type: "auth", token: this.token }));
    });
    socket.addEventListener("message", (ev) => {
      try {
        const msg = JSON.parse(typeof ev.data === "string" ? ev.data : "") as { type?: string; paths?: unknown };
        if (msg?.type === "auth-ok" && !authed) {
          authed = true;
          socket.send(JSON.stringify({ type: "watch", glob })); // Abo erst NACH bestätigtem Auth
          return;
        }
        if (msg?.type === "changed" && authed && Array.isArray(msg.paths)) {
          cb(msg.paths.filter((p): p is string => typeof p === "string"));
        }
      } catch {
        /* fehlerhafte Nachricht ignorieren */
      }
    });
    return () => socket.close();
  }
}

/** Fabrik mit Validierung (Base-URL/Token) — die UI fängt den Wurf als Verbindungs-Fehler ab. */
export function createCliSource(conn: CliConnection): CliSource {
  return new CliSource(conn);
}
